import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import frameSvg from 'assets/icons/frame.svg';
import { Store } from 'components/common/types/Store.types';
import { CodeNameModel } from 'components/common/types';
import { Environment } from 'components/common/types/Deal.types';
import SectionButton from 'lib/SectionButton';
import { SectionButtonTheme } from 'lib/SectionButton/SectionButton.types';
import { PlannerSecondaryPanelType } from 'store/dealManagement/types';
import { ProductFieldName } from 'store/productManagement/reducer.types';
import { changeNewProductField, clearItemInNewProduct, hideSecondaryPanel } from 'store/productManagement/reducer';
import { useOnChangePanelVisibility } from 'components/pages/PCM/hooks/useOnChangePanelVisibility';
import { getIsReadOnly } from 'store/productManagement/selectors';
import { getSelectedChannelsDisplayValue } from './utils';
import ProductChannelsPanel from './ProductChannelsPanel';
import { PERMISSION_LABELS } from '../../consts';

const fieldName = ProductFieldName.CHANNELS;

const ProductChannels: React.FC = () => {
  const dispatch = useDispatch();
  const secondaryPanelType = useSelector((state: Store) => state.productManagement.secondaryPanel);
  const defaultSelectedPermission = useSelector(
    (state: Store) => state.productManagement.newProduct.channels?.selectionPermission,
  );
  const selectedChannels = useSelector((state: Store) => state.productManagement.newProduct.channels?.value);
  const onChangePanelVisibility = useOnChangePanelVisibility();
  const isReadOnly = useSelector(getIsReadOnly);

  const isProductChannelsPanelOpen = secondaryPanelType === PlannerSecondaryPanelType.CHANNEL;

  const handleSubmit = ({
    value,
    selectedPermission,
  }: {
    value: Environment[];
    selectedPermission: CodeNameModel;
  }): void => {
    dispatch(
      changeNewProductField({
        fieldName,
        value,
        selectedPermission,
      }),
    );
    dispatch(hideSecondaryPanel());
  };

  const onRemoveEnvironmentChip = (environmentsToRemove?: string[]): void => {
    if (!environmentsToRemove) {
      dispatch(clearItemInNewProduct({ fieldName }));
      return;
    }

    if (!environmentsToRemove.length || !defaultSelectedPermission) return;

    const environmentsSet = new Set(environmentsToRemove);
    const newSelectedChannels = (selectedChannels || []).filter((value) => !environmentsSet.has(value.name));

    if (newSelectedChannels.length) {
      handleSubmit({
        value: newSelectedChannels,
        selectedPermission: { code: defaultSelectedPermission, name: PERMISSION_LABELS[defaultSelectedPermission] },
      });
    } else {
      dispatch(clearItemInNewProduct({ fieldName }));
    }
  };

  return (
    <>
      <SectionButton
        dataTestId="section-button-channels"
        theme={SectionButtonTheme.SOLID}
        label="Channel"
        onClick={() => onChangePanelVisibility(PlannerSecondaryPanelType.CHANNEL)}
        icon={frameSvg}
        isActive={isProductChannelsPanelOpen}
        selected={getSelectedChannelsDisplayValue(defaultSelectedPermission, selectedChannels)}
        onClear={() => {
          dispatch(clearItemInNewProduct({ fieldName }));
          dispatch(hideSecondaryPanel());
        }}
        onRemoveChip={onRemoveEnvironmentChip}
        isRequired
        isEllipsisDisabled={isReadOnly}
      />
      {isProductChannelsPanelOpen && <ProductChannelsPanel onConfirm={handleSubmit} isReadOnly={isReadOnly} />}
    </>
  );
};

export default ProductChannels;
